import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import merchantService from '../services/merchantService';
import { notifyMerchantUpdated } from '../utils/merchantEvents';
import { canImportMerchantProviders } from '../utils/merchantProviderPermissions';
import Modal from './Modal';
import MasterProviderImportPreview from './MasterProviderImportPreview';

const MerchantProviderCsvActions = ({ merchantId, merchantName, onImported, importDisabled = false }) => {
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [skipIssues, setSkipIssues] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const canExport = hasPermission('merchants', 'export') && hasPermission('merchants', 'view_financial');
  const canImport = canImportMerchantProviders(hasPermission);

  const close = () => {
    if (busy) return;
    setOpen(false); setFile(null); setPreview(null); setResolutions({}); setSkipIssues(false); setError(''); setResult('');
  };
  const exportCsv = async () => {
    setExporting(true); setError('');
    try {
      const blob = await merchantService.exportProviders(merchantId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      try {
        link.href = url; link.download = `${merchantName.replace(/[^a-zA-Z0-9_-]/g, '-')}-Client-Game.csv`;
        document.body.appendChild(link); link.click();
      } finally { link.remove(); URL.revokeObjectURL(url); }
    } catch (err) {
      const data = err.response?.data instanceof Blob ? await err.response.data.text().then(JSON.parse).catch(() => null) : err.response?.data;
      setError(data?.message || err.message || 'Export failed.');
    } finally { setExporting(false); }
  };
  const compare = async (mode = 'dry-run', selectedFile = file, selections = resolutions, skip = skipIssues) => {
    if (!selectedFile || (mode === 'apply' && !preview?.confirmationToken)) return;
    setBusy(true); setError(''); setResult('');
    const confirmationToken = preview?.confirmationToken;
    setPreview(previous => previous ? { ...previous, confirmationToken: null } : null);
    try {
      const response = await merchantService.importProviders(merchantId, { file: selectedFile, mode, skipIssues: skip, resolutions: selections, confirmationToken });
      setPreview({ ...response.data.preview, confirmationToken: response.data.confirmation_token });
      if (mode === 'apply') {
        setResult(`Import completed.${response.data.summary.skipped ? ` ${response.data.summary.skipped} records skipped.` : ''}`);
        notifyMerchantUpdated();
        await onImported?.();
      }
    } catch (err) {
      setPreview(null);
      setError(err.response?.data?.message || err.message || 'Import failed.');
    } finally { setBusy(false); }
  };

  if (!merchantId || (!canExport && !canImport)) return null;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.65rem', flexWrap: 'wrap' }}>
        {canExport && <button type="button" className="btn btn-secondary" disabled={exporting} onClick={exportCsv} title="Export all saved game connections as Filtered Client Game CSV">{exporting ? 'Exporting…' : 'Export CSV'}</button>}
        {canImport && <button type="button" className="btn btn-secondary" disabled={importDisabled} onClick={() => { setError(''); setOpen(true); }} title={importDisabled ? 'Save your game provider edits before importing.' : 'Compare a merchant game CSV before importing'}>Import CSV</button>}
      </div>
      {error && !open && <p role="alert" style={{ color: 'var(--error)', fontSize: '.8125rem' }}>{error}</p>}
      <Modal isOpen={open} onClose={close} title={`Import game providers · ${merchantName}`} maxWidth={preview ? '1200px' : '760px'}>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '.875rem' }}>
            Use the Filtered Client Game CSV, with or without its first blank / Is Client Line column. Selling uses Client Selling Price; OWNLINE enables Is Client Line.
            Choose CSV or System for each difference; Ignore skips the entire CSV row.
            Shortcode, cost, carry forward and billing changes update the shared L1 and list affected merchants. Missing L1 / L0 records are included in the preview.
            Existing connections absent from the file are kept. Nothing is saved until you confirm.
          </p>
          <label>CSV or Excel file<input type="file" className="input" accept=".csv,.xls,.xlsx" disabled={busy}
            onChange={event => {
              const selected = event.target.files?.[0] || null;
              setFile(selected); setResolutions({}); setSkipIssues(false); setPreview(null); setError(''); setResult('');
              if (selected) compare('dry-run', selected, {}, false);
            }} /></label>
          <label className="l1-import-skip-option"><input type="checkbox" checked={skipIssues} disabled={busy}
            onChange={event => { const skip = event.target.checked; setSkipIssues(skip); compare('dry-run', file, resolutions, skip); }} />
            <span><strong>Skip records with issues</strong><small>Only valid records are imported; skipped rows remain listed.</small></span></label>
          {busy && <p role="status">Processing import comparison…</p>}
          {preview && <p style={{ margin: 0, fontWeight: 600 }}>Target merchant: {preview.merchant.name}</p>}
          {error && <p role="alert" style={{ color: 'var(--error)' }}>{error}</p>}
          {preview && <MasterProviderImportPreview preview={preview} variant="merchant" resolutions={resolutions} disabled={busy}
            onResolve={selections => { setResolutions(selections); compare('dry-run', file, selections); }} />}
          {result && <p role="status" style={{ color: 'var(--success)' }}>{result}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.65rem' }}>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={close}>Close</button>
            <button type="button" className="btn btn-secondary" disabled={busy || !file} onClick={() => compare()}>Compare again</button>
            <button type="button" className="btn btn-primary" disabled={busy || !preview?.confirmationToken} onClick={() => compare('apply')}>Confirm import{preview?.summary.skipped ? ` (skip ${preview.summary.skipped})` : ''}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MerchantProviderCsvActions;
